import React, { useState, useRef, useEffect } from "react";
import cancel from "../assets/images/navbar/cancel.png";

interface Option {
  id: string;
  name: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder?: string;
  label: string;
  selectedBgColor?: string;
  selectedTextColor?: string;
  selectedBorderColor?: string;
  isDarkMode?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options",
  label,
  selectedBgColor = "bg-[#FFF5E6]",
  selectedTextColor = "text-[#FF8A00]",
  selectedBorderColor = "border-[#FF8A00]",
  isDarkMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleOption = (optionId: string) => {
    const newSelectedValues = selectedValues.includes(optionId)
      ? selectedValues.filter((id) => id !== optionId)
      : [...selectedValues, optionId];
    onChange(newSelectedValues);
  };

  const handleRemoveOption = (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelectedValues = selectedValues.filter((id) => id !== optionId);
    onChange(newSelectedValues);
  };

  const getSelectedOptions = () => {
    return options.filter((option) => selectedValues.includes(option.id));
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${
        isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'
      }`}>
        {label}
      </label>

      {/* Dropdown Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full min-h-[48px] px-4 py-2 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors cursor-pointer ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
        }`}
      >
        {selectedValues.length === 0 ? (
          <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {getSelectedOptions().map((option) => (
              <span
                key={option.id}
                className={`inline-flex items-center gap-2 px-3 py-1 ${selectedBgColor} ${selectedTextColor} border ${selectedBorderColor} rounded-lg font-Urbanist text-[13px] font-medium`}
              >
                {option.name}
                <button
                  type="button"
                  onClick={(e) => handleRemoveOption(option.id, e)}
                  className="hover:opacity-70 transition-opacity"
                >
                  <img
                    src={cancel}
                    alt="Remove"
                    className="h-[16px] w-[16px]"
                  />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-[250px] overflow-y-auto ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600'
            : 'bg-white border-gray-300'
        }`}>
          {options.length === 0 ? (
            <div className={`px-4 py-3 font-Urbanist text-[14px] ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              No options available
            </div>
          ) : (
            options.map((option) => {
              const isSelected = selectedValues.includes(option.id);
              return (
                <div
                  key={option.id}
                  onClick={() => handleToggleOption(option.id)}
                  className={`px-4 py-3 cursor-pointer font-Urbanist text-[14px] transition-colors flex items-center justify-between ${
                    isDarkMode
                      ? `hover:bg-gray-600 ${isSelected ? 'bg-gray-600' : ''} text-white`
                      : `hover:bg-gray-50 ${isSelected ? 'bg-[#FFF5E6]' : ''} text-gray-900`
                  }`}
                >
                  <span className={isSelected ? "font-semibold" : ""}>
                    {option.name}
                  </span>
                  {isSelected && (
                    <svg
                      className="w-5 h-5 text-[#FF8A00]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
